import { useState, useEffect, ReactNode } from 'react';
import { env } from '@/lib/env';

const PREVIEW_CODE = env.PREVIEW_PASSWORD; // Access code for stakeholders (VITE_PREVIEW_PASSWORD)
const STORAGE_KEY = 'stork_preview_access';

interface PreviewGateProps {
  children: ReactNode;
}

/**
 * Preview Gate
 *
 * DISABLED by default — the site is public. The gate only activates when
 * `VITE_PREVIEW_GATE=true` (used for staging/preview deploys). When enabled it
 * shows a "Coming Soon" page; stakeholders bypass it by either adding
 * `?preview=<code>` to any URL or entering the code on the Coming Soon page.
 * Once bypassed, access is stored in localStorage for 7 days.
 */
export function PreviewGate({ children }: PreviewGateProps) {
  // Not gated — render the app directly. This is the production default.
  if (!env.PREVIEW_GATE) {
    return <>{children}</>;
  }

  return <PreviewGateInner>{children}</PreviewGateInner>;
}

function PreviewGateInner({ children }: PreviewGateProps) {
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [codeInput, setCodeInput] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Check URL parameter first
    const urlParams = new URLSearchParams(window.location.search);
    const previewParam = urlParams.get('preview');

    if (previewParam === PREVIEW_CODE) {
      grantAccess();
      // Remove the parameter from URL for cleaner sharing
      urlParams.delete('preview');
      const newUrl = urlParams.toString()
        ? `${window.location.pathname}?${urlParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      return;
    }

    // Check localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const { expiry } = JSON.parse(stored);
      if (new Date(expiry) > new Date()) {
        setHasAccess(true);
        return;
      }
      // Expired, remove it
      localStorage.removeItem(STORAGE_KEY);
    }

    setHasAccess(false);
  }, []);

  const grantAccess = () => {
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + 7); // 7 days access
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ expiry: expiry.toISOString() }));
    setHasAccess(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (codeInput.toLowerCase() === PREVIEW_CODE.toLowerCase()) {
      grantAccess();
      setError('');
    } else {
      setError('Invalid access code');
    }
  };

  // Loading state
  if (hasAccess === null) {
    return (
      <div className="min-h-screen bg-primary flex items-center justify-center">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }

  // Has access - render the app
  if (hasAccess) {
    return <>{children}</>;
  }

  // Coming Soon page
  return (
    <div className="min-h-screen flex flex-col">
      {/* Hero Section - matches HeroSection.tsx styling */}
      <div className="relative flex-1 flex flex-col bg-primary">
        <div className="absolute inset-0 overflow-hidden">
          {/* Hero background image */}
          <img
            src="/assets/images/hero/banner4.jpg"
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            aria-hidden="true"
          />
          {/* Dark gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-primary/90 via-primary/85 to-primary/95" />
        </div>

        {/* Header */}
        <header className="relative z-10 p-6">
          <div className="max-w-7xl mx-auto">
            <img
              src="/assets/images/logo/TSELogoFull.png"
              alt="Stork Rentals"
              className="h-10 w-auto hidden sm:block brightness-0 invert"
            />
            <img
              src="/assets/images/logo/TSELogo.png"
              alt="Stork Rentals"
              className="h-10 w-auto sm:hidden brightness-0 invert"
            />
          </div>
        </header>

        {/* Main Content */}
        <main className="relative z-10 flex-1 flex items-center justify-center p-6">
          <div className="max-w-2xl mx-auto text-center">
            {/* Icon */}
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-white/10 backdrop-blur rounded-full mb-6">
                <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>

            {/* Text */}
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-4 [text-shadow:_0_2px_12px_rgb(0_0_0_/_40%)]">
              Coming Soon
            </h1>
            <p className="text-xl text-white/90 mb-10 max-w-lg mx-auto [text-shadow:_0_1px_8px_rgb(0_0_0_/_50%)]">
              We're putting the finishing touches on something special.
              Premium baby equipment rentals for families on the go.
            </p>

            {/* Features Preview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
              <div className="bg-white/10 backdrop-blur rounded-xl p-5 border border-white/20">
                <div className="w-10 h-10 bg-primary/30 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="font-semibold text-white mb-1">Premium Quality</h3>
                <p className="text-sm text-white/70">Top-brand strollers, car seats & more</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-5 border border-white/20">
                <div className="w-10 h-10 bg-primary/30 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-white mb-1">Easy Pickup</h3>
                <p className="text-sm text-white/70">Convenient locations across the city</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-5 border border-white/20">
                <div className="w-10 h-10 bg-primary/30 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-white mb-1">Flexible Plans</h3>
                <p className="text-sm text-white/70">Daily, weekly & monthly options</p>
              </div>
            </div>

            {/* Stakeholder Access */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 max-w-sm mx-auto">
              <p className="text-sm text-white/70 mb-4">Team member? Enter access code:</p>
              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="password"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                  placeholder="Access code"
                  className="w-full px-4 py-2 bg-white/10 border border-white/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent text-center"
                />
                {error && <p className="text-destructive/60 text-sm">{error}</p>}
                <button
                  type="submit"
                  className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary transition-colors font-medium"
                >
                  Enter Preview
                </button>
              </form>
            </div>
          </div>
        </main>
      </div>

      {/* Footer - matches Footer.tsx styling */}
      <footer className="bg-foreground/90 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-muted-foreground/70">
            © {new Date().getFullYear()} Stork Rentals. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
